type JsZipModule = typeof import('jszip')
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null
const isJsZipCtor = (value: unknown): value is JsZipModule => typeof value === 'function'
const hasDefaultExport = (value: unknown): value is { default: unknown } =>
  isRecord(value) && 'default' in value

const pickJsZipCtor = (value: unknown): JsZipModule => {
  const direct = isJsZipCtor(value) ? value : null
  const fallback = hasDefaultExport(value) && isJsZipCtor(value.default) ? value.default : null
  const candidate = direct ?? fallback
  if (!candidate) {
    throw new Error('Failed to load JSZip module')
  }
  return candidate
}

let jsZipModulePromise: Promise<JsZipModule> | null = null

const loadJsZip = async (): Promise<JsZipModule> => {
  const ongoing = jsZipModulePromise
  const nextPromise = ongoing ?? import('jszip').then((module) => pickJsZipCtor(module))
  jsZipModulePromise = nextPromise
  return nextPromise
}

export const MAX_WIDTH = 4096
const DEFAULT_OUTPUT_MIME: SupportedImageMime = 'image/png'
const DEFAULT_JPEG_QUALITY = 0.92

export type SupportedImageMime = 'image/png' | 'image/jpeg'

export type BitmapSource = CanvasImageSource & { width: number; height: number; close?: () => void }

export type DrawingContext = Pick<CanvasRenderingContext2D, 'drawImage'>

export interface CanvasRenderer {
  width: number
  height: number
  getContext: (type: '2d') => DrawingContext | null
  convertToBlob?: (options?: BlobPropertyBag) => Promise<Blob>
  toBlob?: HTMLCanvasElement['toBlob']
}

export type CanvasFactory = (size: { width: number; height: number }) => CanvasRenderer

export type BitmapCreator = (blob: Blob) => Promise<BitmapSource>

export interface SplitRegion {
  startY: number
  height: number
}

export interface ProcessImageOptions {
  zipBaseName?: string
  createBitmap?: BitmapCreator
  createCanvas?: CanvasFactory
  maxWidth?: number
  heicQuality?: number
  outputMime?: SupportedImageMime
  containerWidth?: number
}

export interface ProcessImageResult {
  blobs: Blob[]
  zipBlob: Blob
  mimeType: SupportedImageMime
  slices: ImageSlice[]
}

export interface ImageSlice {
  width: number
  height: number
}

export const shouldConvertHeic = (file: File): boolean => {
  const lowerCaseName = file.name.toLowerCase()
  const hasHeicMime = file.type === 'image/heic' || file.type === 'image/heif'
  const hasHeicExt =
    lowerCaseName.endsWith('.heic') ||
    lowerCaseName.endsWith('.heif') ||
    lowerCaseName.endsWith('.heifs')

  return hasHeicMime || hasHeicExt
}

export const calculateResizeTarget = (width: number, height: number, maxWidth: number): {
  width: number
  height: number
} => {
  const needsResize = width > maxWidth
  const scale = needsResize ? maxWidth / width : 1

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  }
}

export const computeSplitRegions = (height: number, gapPx: number): SplitRegion[] => {
  const clampedGap = Math.max(gapPx, 0)
  const effectiveHeight = height - clampedGap * 3
  const segmentHeight = Math.floor(effectiveHeight / 4)
  const remainder = effectiveHeight - segmentHeight * 3
  const step = segmentHeight + clampedGap

  return [
    { startY: 0, height: segmentHeight },
    { startY: step, height: segmentHeight },
    { startY: step * 2, height: segmentHeight },
    { startY: step * 3, height: remainder }
  ]
}

export const zipBlobs = async (
  blobs: Blob[],
  baseName: string,
  extension: 'png' | 'jpg' = 'png'
): Promise<Blob> => {
  const JsZip = await loadJsZip()
  const zip = new JsZip()

  blobs.forEach((blob, index) => {
    zip.file(`${baseName}_${String(index + 1)}.${extension}`, blob)
  })

  return zip.generateAsync({ type: 'blob' })
}

export const resizeImageBitmap = async (
  bitmap: BitmapSource,
  maxWidth: number,
  createCanvas: CanvasFactory,
  createBitmap: BitmapCreator,
  outputMime: SupportedImageMime
): Promise<BitmapSource> => {
  const target = calculateResizeTarget(bitmap.width, bitmap.height, maxWidth)
  const needsResize = bitmap.width > target.width
  const resizedBlob = needsResize
    ? await renderBitmapToBlob(bitmap, target, outputMime, createCanvas)
    : null
  const resizedBitmap = resizedBlob ? await createBitmap(resizedBlob) : bitmap
  const closer = typeof bitmap.close === 'function' ? bitmap.close.bind(bitmap) : null
  const shouldClose = needsResize && closer
  if (shouldClose) {
    closer()
  }
  return resizedBitmap
}

export const splitImageIntoBlobs = async (
  bitmap: BitmapSource,
  regions: SplitRegion[],
  mimeType: SupportedImageMime,
  createCanvas: CanvasFactory
): Promise<{ blobs: Blob[]; slices: ImageSlice[] }> => {
  const pairs = await Promise.all(
    regions.map(async (region) => {
      const canvas = createCanvas({ width: bitmap.width, height: region.height })
      const context = ensureContext(canvas.getContext('2d'))

      context.drawImage(
        bitmap,
        0,
        region.startY,
        bitmap.width,
        region.height,
        0,
        0,
        bitmap.width,
        region.height
      )

      const blob = await convertCanvasToBlob(canvas, mimeType)

      return { blob, slice: { width: bitmap.width, height: region.height } satisfies ImageSlice }
    })
  )

  return {
    blobs: pairs.map((pair) => pair.blob),
    slices: pairs.map((pair) => pair.slice)
  }
}

export const processImageFile = async (
  file: File,
  gapCssPx: number,
  containerWidth: number,
  options?: ProcessImageOptions
): Promise<ProcessImageResult> => {
  const baseName = options?.zipBaseName ?? deriveBaseName(file.name)
  const createBitmap = options?.createBitmap ?? defaultCreateBitmap
  const createCanvas = options?.createCanvas ?? defaultCanvasFactory
  const maxWidth = options?.maxWidth ?? MAX_WIDTH
  const heicQuality = options?.heicQuality ?? DEFAULT_JPEG_QUALITY
  const outputMime = options?.outputMime ?? DEFAULT_OUTPUT_MIME
  const requiresHeic = shouldConvertHeic(file)
  const sourceBlob = requiresHeic ? await convertHeicBlob(file, heicQuality) : file
  const bitmap = await createBitmap(sourceBlob)
  const resizedBitmap = await resizeImageBitmap(
    bitmap,
    maxWidth,
    createCanvas,
    createBitmap,
    outputMime
  )
  const safeContainerWidth = containerWidth > 0 ? containerWidth : resizedBitmap.width
  const gapSourcePx = Math.max(0, Math.round((gapCssPx * resizedBitmap.width) / safeContainerWidth))
  const regions = computeSplitRegions(resizedBitmap.height, gapSourcePx)
  const splitResult = await splitImageIntoBlobs(resizedBitmap, regions, outputMime, createCanvas)
  const zipBlob = await zipBlobs(splitResult.blobs, baseName, outputMime === 'image/jpeg' ? 'jpg' : 'png')

  return { blobs: splitResult.blobs, zipBlob, mimeType: outputMime, slices: splitResult.slices }
}

const deriveBaseName = (fileName: string): string => {
  const segments = fileName.split('.')
  const base = segments.length > 1 ? segments.slice(0, -1).join('.') : fileName
  return base.length > 0 ? base : 'image'
}

const ensureContext = (context: DrawingContext | null): DrawingContext => {
  const valid = context && typeof context.drawImage === 'function'
  if (valid) {
    return context
  }
  throw new Error('2d context is not available')
}

const defaultCreateBitmap: BitmapCreator = (blob) => {
  const creator = typeof createImageBitmap === 'function' ? createImageBitmap : null
  return creator
    ? creator(blob)
    : Promise.reject(new Error('createImageBitmap is not available in this environment'))
}

const defaultCanvasFactory: CanvasFactory = (size) => {
  const offscreenCanvas = typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(size.width, size.height) : null
  const fallbackCanvas =
    offscreenCanvas ??
    (() => {
      const hasDocument = typeof document !== 'undefined'
      const canvasElement = hasDocument ? document.createElement('canvas') : null
      const withSize = canvasElement ?? (() => {
        throw new Error('Canvas is not available')
      })()
      withSize.width = size.width
      withSize.height = size.height
      return withSize
    })()

  const toBlob =
    'toBlob' in fallbackCanvas && typeof fallbackCanvas.toBlob === 'function'
      ? fallbackCanvas.toBlob.bind(fallbackCanvas)
      : undefined

  return {
    width: fallbackCanvas.width,
    height: fallbackCanvas.height,
    getContext: () => {
      const context2d = fallbackCanvas.getContext('2d')
      return context2d && 'drawImage' in context2d ? context2d : null
    },
    convertToBlob:
      'convertToBlob' in fallbackCanvas ? fallbackCanvas.convertToBlob.bind(fallbackCanvas) : undefined,
    toBlob
  }
}

const convertCanvasToBlob = (
  canvas: CanvasRenderer,
  mimeType: SupportedImageMime,
  quality?: number
): Promise<Blob> => {
  const convertToBlob = canvas.convertToBlob
  if (convertToBlob) {
    return convertToBlob({ type: mimeType })
  }
  const toBlob = canvas.toBlob
  if (toBlob) {
    return new Promise<Blob>((resolve, reject) => {
      toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
            return
          }
          reject(new Error('Failed to create Blob'))
        },
        mimeType,
        quality
      )
    })
  }
  return Promise.reject(new Error('Canvas toBlob is not available'))
}

const renderBitmapToBlob = async (
  bitmap: BitmapSource,
  size: { width: number; height: number },
  mimeType: SupportedImageMime,
  createCanvas: CanvasFactory
): Promise<Blob> => {
  const canvas = createCanvas(size)
  const context = ensureContext(canvas.getContext('2d'))

  context.drawImage(
    bitmap,
    0,
    0,
    bitmap.width,
    bitmap.height,
    0,
    0,
    size.width,
    size.height
  )

  return convertCanvasToBlob(canvas, mimeType, mimeType === 'image/jpeg' ? DEFAULT_JPEG_QUALITY : undefined)
}

const convertHeicBlob = async (file: File, quality: number): Promise<Blob> => {
  const { default: heic2any } = await import('heic2any')
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality })
  const singleBlob = Array.isArray(converted) ? converted[0] : converted

  return singleBlob
}
